<template>
  <div class="sso-auth-form">
    <!-- Loading state during auto-login -->
    <div v-if="loading" class="text-center">
      <div class="mb-4">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <p class="text-muted">{{ $t('common.loading') }}</p>
    </div>

    <!-- SSO Confirmation UI (when not anonymous) -->
    <div v-else class="sso-confirmation text-center">
      <Icon name="grLogin" width="36" class="mb-3"/>

      <div class="mb-4">
        <h4 class="sso-confirmation__title">{{ $t('sso.title') }}</h4>
        <p class="text-muted">{{ $t('sso.subtitle', {email: userEmail}) }}</p>
      </div>

      <!-- Email Input (disabled) -->
      <div class="form-group mb-4">
        <label for="sso-email" class="form-label small text-muted">
          {{ $t('common.email') }}
        </label>
        <input
          type="email"
          id="sso-email"
          class="form-control"
          :value="userEmail"
          disabled
        />
      </div>

      <!-- Continue Button -->
      <b-button
        class="w-100"
        variant="primary"
        size="lg"
        @click="performSsoLogin"
      >
        {{ $t('sso.btn_login') }}
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import Icon from '@/domain/icon/Icon.vue'
import { AuthProviders } from '../../constants/authProviders'

/**
 * SSO Auto-Login Component
 *
 * Handles SSO authentication when SSO parameters are present in URL.
 * - Shows email confirmation UI by default
 * - Auto-authenticates when anon=true or noAuthMsg=true
 *
 * Usage: <SsoAuthForm @success="handleSuccess" @error="handleError" />
 *
 * This component is shown when ?t=xxx&k=yyy is detected in URL.
 */
export default {
  name: 'SsoAuthForm',

  components: {
    Icon
  },

  data() {
    return {
      loading: false
    }
  },

  computed: {
    ...mapGetters('auth', ['hasAcceptedRequiredPolicies', 'authProviderFactory']),

    /**
     * Get SSO token from URL query params
     */
    ssoToken() {
      return this.$route.query.t
    },

    /**
     * Get SSO integration ID from URL query params
     */
    integrationId() {
      return this.$route.query.k
    },

    /**
     * Extract email from SSO JWT token
     */
    userEmail() {
      if (!this.ssoToken) return ''

      try {
        const token = this.jwtDecode(this.ssoToken)
        return token.payload.email || ''
      } catch (error) {
        console.error('Failed to decode SSO token:', error)
        return ''
      }
    },

    /**
     * Get redirect route name from query params with proper mapping
     */
    redirectRoute() {
      const redirectParam = this.$route.query.redirect || this.$route.query.next

      // Map backend redirect values to Vue Router route names
      const redirectMap = {
        'checkins': 'checkins-list',
        'checkins-list': 'checkins-list',
        'homepage': 'homepage'
      }

      return redirectMap[redirectParam] || 'checkins-list'
    },

    /**
     * Check if anonymous access (auto-login without UI)
     */
    isAnonymousAccess() {
      return this.$route.query.hasOwnProperty('anon')
    },

    /**
     * Check if noAuthMsg is set (auto-login without UI)
     */
    noAuthMsg() {
      return ['true', '1'].includes(this.$route.query.noAuthMsg)
    }
  },

  mounted() {
    // Auto-login if anonymous or noAuthMsg flag is set
    if (this.isAnonymousAccess || this.noAuthMsg) {
      this.performSsoLogin()
    }
  },

  methods: {
    ...mapActions('auth', ['authenticate']),

    /**
     * Perform SSO authentication and redirect
     */
    async performSsoLogin() {
      if (!this.ssoToken || !this.integrationId) {
        this.$emit('error', new Error('SSO token or integration ID not found in URL'))
        return
      }

      this.loading = true

      try {
        // Set preview checkin flag
        this.$store.commit('SET_IS_PREVIEW_CHECKIN', true)

        // Get SSO provider from factory
        const provider = this.authProviderFactory.getProvider(AuthProviders.SSO)

        if (!provider) {
          throw new Error('SSO provider not available')
        }

        // Authenticate with SSO using universal authenticate action
        const response = await this.authenticate({
          method: AuthProviders.SSO,
          credentials: {
            token: this.ssoToken,
            integration_id: this.integrationId
          },
          remember: true
        })

        // Determine redirect destination based on picacode from SSO response
        let redirectOptions

        if (response && response.picacode) {
          // With picacode: save to sessionStorage and redirect to checkins-detail
          sessionStorage.setItem('picacode', response.picacode)
          redirectOptions = {
            name: 'checkins-detail',
            params: { picacode: response.picacode }
          }
        } else {
          // Without picacode: use redirect parameter
          redirectOptions = { name: this.redirectRoute }
        }

        // Check privacy policies
        if (!this.hasAcceptedRequiredPolicies) {
          // Redirect to terms page with next destination
          await this.$router.push({
            name: 'terms',
            params: { next: this.redirectRoute }
          })
        } else {
          // Redirect to intended destination (checkins-detail if picacode, otherwise redirectRoute)
          await this.$router.push(redirectOptions)
        }

        // Emit success event
        this.$emit('success')

      } catch (error) {
        console.error('SSO authentication failed:', error)
        this.$emit('error', error)
        this.loading = false
      }
    },

    /**
     * Decode JWT token to extract payload
     */
    jwtDecode(token) {
      const parts = token.split('.')
      return {
        raw: token,
        header: JSON.parse(window.atob(parts[0])),
        payload: JSON.parse(window.atob(parts[1]))
      }
    }
  }
}
</script>

<style scoped>
.sso-auth-form {
  padding: 2rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.sso-confirmation {
  max-width: 400px;
  margin: 0 auto;
}

.sso-confirmation__title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.form-control:disabled {
  background-color: #f8f9fa;
  opacity: 0.7;
}
</style>
